import { default as diff } from 'changeset';
import { VoiceDropdown } from '@app/page-components';
import { createUuid } from '@app/utilities';
import {
    AudioType,
    defaultLanguage,
    ObjectClass
} from '@app/types';
import React from 'react';
import ResponseListGroup from './ResponseListGroup';
import SessionPanel from './components/test-tool/SessionPanel';
import Exporter from "../utilities/Exporter";
import FileSaver from "file-saver";
import ReactDOM from "react-dom";
import UnitTestDialog from "./dialogs/UnitTestDialog";
import Utility from "../utilities/Utility";
import { defaultServiceContext } from '../contexts';
import DesignerSetting from '../DesignerSetting';

const G_SESSION_ID_PARAM = "session.parentsessionid";

const {
    processEngineService,
    textToPromptService
} = defaultServiceContext;

const channelOptions = [
    {value: "ALEXA", label: "ALEXA"},
    {value: "EMAIL", label: "EMAIL"},
    {value: "FACEBOOK", label: "FACEBOOK"},
    {value: "MOBILE", label: "MOBILE"},
    {value: "SMS", label: "SMS"},
    {value: "SOCIAL_CHAT", label: "SOCIAL_CHAT"},
    {value: "TWITTER", label: "TWITTER"},
    {value: "VOICE", label: "VOICE"},
    {value: "WEB_CHAT", label: "WEB_CHAT"}
];

const errorOptions = [
    {value: "NO_ANALYSTS_ASSIGNED", label: "NO_ANALYSTS_ASSIGNED"},
    {value: "NO_ANALYST_RESPONSE", label: "NO_ANALYST_RESPONSE"},
    {value: "ASSIGNMENT_REQUEST_TIMEOUT", label: "ASSIGNMENT_REQUEST_TIMEOUT"},
    {value: "ASSIGNMENT_DENIAL", label: "ASSIGNMENT_DENIAL"},
    {value: "ASSIGNMENT_REQUEST_ERROR", label: "ASSIGNMENT_REQUEST_ERROR"}
];

const filterInfo = {
    intent: {name: "Intents", iconClass: "icon-mic"},
    error: {name: "Errors", iconClass: "icon-user-x"},
    dsid: {name: "DSID", iconClass: "icon-headphones"},
    prompt: {name: "Prompts", iconClass: "icon-message-square"},
    waitPrompt: {name: "Wait Prompts", iconClass: "icon-clock"},
    reportTag: {name: "Report Tags", iconClass: "icon-activity"},
    nodes: {name: "Nodes Hit", iconClass: "icon-disc"},
    unitTest: {name: "Unit Tests", iconClass: "icon-check-circle"}
};

const filterPresets = {
    clear: {
        intent: false,
        error: false,
        dsid: false,
        prompt: false,
        waitPrompt: false,
        reportTag: false,
        nodes: false,
        unitTest: false
    },
    client: {
        intent: true,
        error: true,
        dsid: false,
        prompt: true,
        waitPrompt: true,
        reportTag: false,
        nodes: false,
        unitTest: true
    },
    designer: {
        intent: true,
        error: true,
        dsid: true,
        prompt: true,
        waitPrompt: true,
        reportTag: false,
        nodes: false,
        unitTest: true
    },
    developer: {
        intent: true,
        error: true,
        dsid: true,
        prompt: true,
        waitPrompt: true,
        reportTag: true,
        nodes: true,
        unitTest: true
    }
};

export default class TestToolWidget extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            endpoint: DesignerSetting.s.processEngineEndpoints[0],
            endpointHealthy: false,
            endpointIsPE: null,
            endpointVersion: null,
            sendingRequest: false,
            currentSessionId: null,
            sessionResults: [],
			sessionStarted: false,
            sessionEnded: false,
			intentOptions: [],
            recognitionVariables: [],
            secondsToResponse: 0,
            autoResponse: true,
            selectedIntents: [{name: null, value: ""}],
            selectedError: null,
            sessionType: "VOICE",
            ani: "",
            dnis: "",
            source: "",
            filterPreset: "developer",
            replayQueue: [],
            unitTests: [],
            unitTestResults: [],
            graphQLSchemas: [],
            useMockData: true
        };

        this.state.filterTypes = {...filterPresets[this.state.filterPreset]};
        
        this.eventHandler = this.handleEvent.bind(this);
        this.handleEndpoint = this.handleEndpoint.bind(this);
        this.handleStartSession = this.handleStartSession.bind(this);
        this.handleEndSession = this.handleEndSession.bind(this);
        this.handleStartOver = this.handleStartOver.bind(this);
        this.handleSessionParams = this.handleSessionParams.bind(this);
        this.handleIntentName = this.handleIntentName.bind(this);
        this.handleIntentValue = this.handleIntentValue.bind(this);
        this.handleRemoveIntent = this.handleRemoveIntent.bind(this);
        this.handleAddIntent = this.handleAddIntent.bind(this);
        this.handleRunIntent = this.handleRunIntent.bind(this);
        this.handleSubmitError = this.handleSubmitError.bind(this);
        this.handleImport = this.handleImport.bind(this);
        this.handleDownload = this.handleDownload.bind(this);
        this.handleFilter = this.handleFilter.bind(this);
        this.handleFilterPreset = this.handleFilterPreset.bind(this);
        this.handleRunTests = this.handleRunTests.bind(this);
        this.handleShowTest = this.handleShowTest.bind(this);
        this.useMockDataToggle = this.useMockDataToggle.bind(this);

        this.timer = null;
    }
    
    generateSessionID() {
        // via https://stackoverflow.com/a/105074/55452
        function s4() {
            return Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
        }
        return s4() + s4() + s4() + s4() + s4() + s4() + s4() + s4();
    }

    componentDidMount() {
        this.initIntents();
        this.checkEndpointHealth(this.state.endpoint);
        this.loadUnitTests();
        this.setState({graphQLSchemas: this.props.serialized.graphQLSchemas})
        document.addEventListener("ev-unit-test-change", this.eventHandler);
        document.addEventListener("ev-unit-test-create", this.eventHandler);
        document.addEventListener("ev-unit-test-rename", this.eventHandler);
        document.addEventListener("ev-unit-test-delete", this.eventHandler);
        document.addEventListener("ev-unit-test-download", this.eventHandler);
    }

    componentWillUnmount() {
        document.removeEventListener("ev-unit-test-change", this.eventHandler);
        document.removeEventListener("ev-unit-test-create", this.eventHandler);
        document.removeEventListener("ev-unit-test-rename", this.eventHandler);
        document.removeEventListener("ev-unit-test-delete", this.eventHandler);
        document.removeEventListener("ev-unit-test-download", this.eventHandler);
    }

    componentDidUpdate(prevProps) {
        const schemasDiff = diff(prevProps.serialized.graphQLSchemas, this.state.graphQLSchemas);
        
        // this rerenders the session panel mock schema UI
        if(schemasDiff.length > 0){
            this.setState({graphQLSchemas: prevProps.serialized.graphQLSchemas})
        }

        // scroll to bottom
        this.messagesEnd.scrollIntoView({ behavior: "smooth" });
    }

    useMockDataToggle(value) {
        this.setState({useMockData: value});
    }

    handleEvent(event) {
        if (event.type === "ev-unit-test-change") {
            this.loadUnitTests();
        }
        if (event.type === "ev-unit-test-create") {
            if (event.detail.name) {
                window.apm.addUnitTest({
                    id: createUuid(),
                    name: event.detail.name,
                    tests: event.detail.tests ? event.detail.tests : this.state.sessionResults
                });
                this.loadUnitTests();
            }
        }
        if (event.type === "ev-unit-test-rename") {
            // if no name provided, show popup to ask for a name
            // called by ContextMenuWidget.renderUnitTestMenu()
            if (!event.detail.name) {
                const oldName = this.state.unitTests.find(t => t.id === event.detail.id).name;
                window.apm.popup("Rename",
                    '<div class="unit_test_rename_container"></div>',
                    '<button class="w2ui-btn secondary" onclick="w2popup.close();">' + window.l.popup.close + '</button> ' +
                    '<button class="w2ui-btn primary add-button">Rename</button> ',
                    [
                        ".unit_test_rename_container",

                        () => {
                            ReactDOM.render(<UnitTestDialog id={event.detail.id} name={oldName}/>, $(".unit_test_rename_container").get(0));
                            $(".w2ui-popup").find(".add-button").on("click", function () {
                                document.dispatchEvent(new CustomEvent("ev-dialog-add"));
                            });
                        
                        }
                    ], 
                    500
                );
                return;
            }
            // if name is provided, rename the unit test
            // called by UnitTestDialog.handleAdd()
            window.apm.renameUnitTest(event.detail.id, event.detail.name);
            this.loadUnitTests();
        }
        if (event.type === "ev-unit-test-delete") {
            event.detail.forEach(window.apm.removeUnitTest);
            this.loadUnitTests();
        }
        if (event.type === "ev-unit-test-download") {
            const test = this.state.unitTests.find(t => t.id === event.detail.id);
            const filename = `${this.props.serialized.client}_${this.props.serialized.application}_${this.props.serialized.version}_${test.name}`;
            FileSaver.saveAs(new Blob([JSON.stringify(test.tests, null, 4)], { type: "text/plain;charset=utf-8" }), `${filename}.json`);
        }
    }

    loadUnitTests() {
        this.setState({
            unitTests: window.apm.serialized.unitTests.sort((a, b) => a.name.localeCompare(b.name))
        });
    }

    updateResults(result){
        this.setState({sessionResults: [...this.state.sessionResults, result]});
    }

    countdown() {
        if (this.state.sessionStarted && this.state.autoResponse && this.state.secondsToResponse) {
            const secondsToResponse = this.state.secondsToResponse - 1;
            this.setState({secondsToResponse});
            if (secondsToResponse === 0) {
                this.appRun({
                    "type": "recognition",
                    "sessionId": this.state.currentSessionId,
                    "recognition": {
                        "transcript": "",
                        "intents": [{name: "SILENCE", value: "", type: "value"}]
                    }
                });
            }
        }
    }

    appRun(requestData, callback, expected, results){

        const { endpoint } = this.state;
        const serialized = this.props.serialized ?? window.apm.serialized;
        const { branchId, version } = serialized;

        const self = this;

        self.setState({
            sendingRequest: true,
            secondsToResponse: 0
        });
        
        const headers = {
            endpoint,
            [G_SESSION_ID_PARAM]: this.state.currentSessionId,
        }

        processEngineService.runTest(branchId, version, requestData, headers)
            .then(response => {
                self.setState({sendingRequest: false}, async () => {
                    const {type} = response;
                    if (requestData.type === "startSession" || requestData.type === "testStartSession") {
                        self.setState({
                            sessionStarted: true,
                            ani: requestData.parameters.ani,
                            dnis: requestData.parameters.dnis,
                            channel: requestData.sessionType
                        });
                        clearInterval(self.timer);
                        self.timer = setInterval(() => self.countdown(), 1000);
                    }
                    // ignore if not current session
                    if (requestData.sessionId !== this.state.currentSessionId) {
                        return;
                    }
                    switch (type) {
                        case "output":
                            break;
                        case "endSession":
                            self.setState({sessionEnded: true});
                            clearInterval(self.timer);
                            break;
                        case "input":
                            if (response.input.config && response.input.config.noInputTimeout) {
                                let noInputTimeout = response.input.config.noInputTimeout;

                                // replace engine's noInputTimeout with local noInputTimeout if any
                                const localInput = window.apm.serialized.components.objects.find(o => (o._class === ObjectClass.input || o._class === ObjectClass.dtmf) && o.name === response.input.id);
                                if (localInput) {
                                    if (localInput._class === ObjectClass.input) {
                                        noInputTimeout = localInput.props.config.noInputTimeout || 0;
                                    }
                                    if (localInput._class === ObjectClass.dtmf && localInput.props.config.inputTimeOut && localInput.props.config.inputTimeOut[0]) {
                                        noInputTimeout = localInput.props.config.inputTimeOut[0].value || 0;
                                    }
                                }
                                response.input.config.noInputTimeout = noInputTimeout;
                                self.setState({secondsToResponse: Math.ceil(noInputTimeout / 1000)});
                            }
                            const intent = {name: null, value: "", type: "value"};
                            if (response.input.expectedIntentName) {
                                intent.name = response.input.expectedIntentName;
                            }
                            if (response.input.config.inputType === "DOB_CAPTURE" || response.input.config.inputType === "FUTURE_DATE_CAPTURE") {
                                intent.type = "date";
                            }
                            const recovars = response.input.recognitionVariables;
                            const list = [];
                            if (Object.keys(recovars).length) {
                                for (let key in recovars) {
                                    if (recovars.hasOwnProperty(key)) {
                                        for (let i = 0; i < recovars[key].length; i++) {
                                            const option = recovars[key][i];
                                            if (option) {
                                                list.push(recovars[key][i]);
                                            }
                                        }
                                    }
                                }
                            }
                            self.setState({
                                recognitionVariables: list,
                                selectedIntents: [intent]
                            });
                            break;
                        default:
                            self.setState({
                                recognitionVariables: [],
                                selectedIntents: [{name: null, value: ""}]
                            });
                    }
                    if (expected) {
                        if (response.type !== "output") {
                            if (!response) {
                                response = {}
                            }
                            const ignoredPrompts = [...window.apm.serialized.fillerPrompts.wait, ...window.apm.serialized.fillerPrompts.typing];
                            const expectedPrompts = expected.prompts ? expected.prompts.filter(p => !ignoredPrompts.some(ip => ip.name === p.name)).map(p => p.name).join(", ") : "";
                            const actualPrompts = response.prompts ? response.prompts.filter(p => !ignoredPrompts.some(ip => ip.name === p.name)).map(p => p.name).join(", ") : "";
                            if (expectedPrompts !== actualPrompts) {
                                response.unitTestError = {
                                    message: `Prompt assertion failed: Expected: "${expectedPrompts}"; Actual: "${actualPrompts}"`
                                };
                            }
                        }
                        results.sessionResults.push({
                            request: requestData,
                            response: response
                        });
                    } else {
                        if (response.prompts && response.prompts.length) {
                            if (self.state.voice) {
                                await textToPromptService.playAudio({
                                    language: {
                                        language: defaultLanguage,
                                        voice: '',
                                        ttsFallBackVoice: self.state.voice
                                    },
                                    serialized,
                                    text: response.prompts.map(p => p.transcript).join(" ")
                                });
                            }
                        }
                        self.updateResults({
                            request: requestData,
                            response: response ? response : {
                                errorMessage: "Engine returns empty response. Possibly due to engine error/timeout. Session ended."
                            }
                        });
                        if (!response) {
                            self.setState({sessionEnded: true});
                        }
                    }
                    if (response.type === "output") {
                        return this.appRun({
                            "type": "resume",
                            "sessionId": this.state.currentSessionId
                        }, callback, expected, results);
                    }
                    if (callback) callback(response);
                });
            })
            .catch(async (error) => {
                let errorMessage = `Engine error: Empty response with error code ${error.status}.`;
                if (error instanceof Response) {
                    errorMessage = await error.text();
                } else if (error instanceof Error) {
                    errorMessage = error.message;
                }

                self.setState({sendingRequest: false});
                self.updateResults({
                    request: requestData,
                    response: { errorMessage }
                });
                console.error(error)
            })
    }

    replay() {
        if (this.state.replayQueue.length === 0) return;
        const request = this.state.replayQueue.shift().request;
        // replace with current session id
        request.sessionId = this.state.currentSessionId;
        this.setState({
            replayQueue: this.state.replayQueue
        }, () => {
            this.appRun(request, () => {
                this.replay();
            });
        });
    }

    test() {
        const unitTestResults = this.state.unitTestResults;
        
        this.initMockDataAccessRecords(unitTestResults);
        
        const remainingTests = unitTestResults.filter(utr => utr.queue.length);
        if (remainingTests.length === 0) {
            this.setState({
                sessionEnded: true
            });
            this.handleShowTest(this.state.unitTestResults[this.state.unitTestResults.length - 1]);
            return;
        }
        const utr = remainingTests[0];
        const test = utr.queue.shift();
        const request = test.request;
        let response = test.response;

        // skip resume calls and extract final result
        for (let i = 0; i < utr.queue.length; i++) {
            const t = utr.queue[i];
            if (t.request.type === "resume") {
                utr.passed++;
                t.skipped = true;
                response = t.response;
            } else {
                break;
            }
        }
        utr.queue = utr.queue.filter(t => !t.skipped);

        // replace with current session id
        request.sessionId = utr.sessionId;
        this.setState({
            currentSessionId: request.sessionId,
            unitTestResults: unitTestResults
        }, () => {
            this.appRun(request, response => {
                if (response.unitTestError) {
                    utr.failed++;
                } else {
                    utr.passed++;
                }
                this.setState({
                    unitTestResults: unitTestResults,
                });
                if (utr.queue.length === 0) {
                    utr.results.currentSessionId = this.state.currentSessionId;
                    utr.results.sessionType = this.state.sessionType;
                    utr.results.ani = this.state.ani;
                    utr.results.dnis = this.state.dnis;
                    utr.results.source = this.state.source;
                }
                this.test();
            }, response, utr.results);
        });
    }

    /**
     * Loads saved unit test data to start session
     * @param {Object} unitTestResults array of unit tests
     */

    initMockDataAccessRecords(unitTestResults) {
        const useMocked = this.state.useMockData;

        unitTestResults.forEach(unitTest => {
            if(unitTest.queue.length > 0) {
                const initialRequest = unitTest.queue[0].request;
                // See
                // CORE-29347
                if(initialRequest.type === "startSession"){
                    initialRequest.mockGraphQLSchemaNames = [];
                    initialRequest.type = "testStartSession";
                }
                
                const requestType = initialRequest.type;

                // CORE-29347
                // "startSession" = test created in QA; "testStartSession" test created in dev/uat
                if(requestType === "testStartSession"){
                    // If the Unit Test comes with mock data, we want to use it in our session request
                    const mockData = initialRequest.mockDataAccessRecords || [];
                    
                    // If the "Use Mock Data" switch is set to false,
                    // then we pass an empty array regardless of whether the unit test has mock data
                    if(!useMocked) {
                        initialRequest.mockDataAccessRecords = [];

                    }

                    // If the "Use Mock Data" switch is set to true,
                    // and the Unit Test doesn't already have mock data, 
                    // then we create it by retrieving data access records from the previous responses
                    if(useMocked && mockData.length === 0){
                        unitTest.queue.forEach(test => {
                            if(test.response.graphEvents){
                                const graphEvents = test.response.graphEvents;
                                
                                graphEvents.forEach(event =>{

                                    if(event.type === "dataAccessExitEvent"){
                                        let thedata = {};
                                        thedata = event.dataAccessRecord;
                                        mockData.push(thedata);
                                    }
                                });
                            }
                        });
                        
                        // If the Unit Test doesn't already have mock data, 
                        // and we find no data access records, then we pass the empty array
                        if(mockData.length > 0) {
                            initialRequest.mockDataAccessRecords = mockData;
                        }
                    }
                }
            }
        });
    }

    initIntents(){
        const intents = window.apm.knowledgeBase.getUniqueIntents();
        const intentOptions = intents.map(intent => {
            return {
                label: intent.name,
                value: intent.name
            }
        });
        const sorted = intentOptions.sort((a,b) => (a.label > b.label) ? 1 : -1);

        this.setState({intentOptions: sorted});
    }

    /**
     * Change endpoint.
     * @param {string} endpoint
     */
    handleEndpoint(endpoint) {
        this.checkEndpointHealth(endpoint);
        this.setState({ endpoint });
    }

    /**
     * Calls actuator for health and info responses
     * @param {*} endpoint 
     */
    checkEndpointHealth(endpoint) {

        const healthUrl = endpoint+"/actuator/health";
        const infoUrl = endpoint+"/actuator/info";

        fetch("/actuator/health", {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                url: healthUrl,
            })
        }).then(res => res.json()).then(health => {

            if(health.error){
                const endpointHealth =  health.error === 503 ? this.checkEndpointServices(health.data) : false;

                this.setState({endpointHealthy: endpointHealth});
            } else {
                const endpointHealth = this.checkEndpointServices(health);

                this.setState({endpointHealthy: endpointHealth});
            }
        });
        
        fetch("/actuator/info", {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                url: infoUrl,
            })
        }).then(res => res.json()).then(info => {
            if (info.error) {
                this.setState({endpointIsPE: false, endpointVersion: null});
            } else {
                const endpointIsPE = info?.applicationName === "ProcessEngine" || info?.build?.artifact === "process-engine-server";
                const endpointVersion = info?.build?.version ?? null;

                if (!endpointIsPE) {
                    alert('Specified endpoint is not a valid Process Engine endpoint. Please contact development team.');
                }
                this.setState({ endpointIsPE, endpointVersion });
            }
        });
    }

    checkEndpointServices(health) {
        const excludedServices = ["externalAudioHealthComposite",
                                  "graphQLHealthComposite",
                                  "humanAssistedActions"];
        const services = health.components;
        
        Object.keys(services).forEach(service => {

            if(services[service].status === "DOWN" && !excludedServices.includes(service)) {
                return false;
            }
        });

        return true;
    }

    handleIntentName(intent, idx) {
        const intents = this.state.selectedIntents;
        intents[idx].name = intent;
        this.setState({selectedIntents: intents});
    }

    handleIntentValue(value, idx){
        const intents = this.state.selectedIntents;
        intents[idx].value = value;
        this.setState({selectedIntents: intents});
    }

    handleRemoveIntent() {
        this.setState({selectedIntents: this.state.selectedIntents.slice(0, -1)});
    }

    handleAddIntent() {
        this.setState({selectedIntents: [...this.state.selectedIntents, {name: null, value: ""}]});
    }

    handleRunIntent(){
        const intents = this.state.selectedIntents.filter(intent => (intent.name || intent.value));
        intents.map(intent => {
            if (intent.type === "date") {
                const dateComponent = intent.value.split("-");
                intent.value = `MM/dd/yyyy|${dateComponent[1]}/${dateComponent[2]}/${dateComponent[0]}`
            }
        });
        if (this.state.selectedIntents.length) {
            const intentRequest = {
                "type": "recognition",
                "sessionId": this.state.currentSessionId,
                "recognition": {
                    "transcript": "",
                    "intents": intents
                }
            };

            this.appRun(intentRequest);
            this.forceUpdate();
        }
    }

    handleSubmitError(){
        const {selectedError} = this.state;
        if (selectedError) {
            const errorRequest = {
                "errorCode": selectedError.value,
                "message": "System Error",
                "sessionId": this.state.currentSessionId,
                "type": "error"
            };

            this.appRun(errorRequest);
        }
    }

    handleStartSession(replay = false, mockSchemaIds = []) {
        this.handleFilterPreset("developer");
        const currentSessionId = this.state.currentSessionId || this.generateSessionID();
        this.setState({
            currentSessionId: currentSessionId,
            sessionResults: [],
            autoResponse: !replay
        }, () => {
            if (replay) {
                this.replay();
            } else {
                const testProperties = {};
                window.apm.serialized.appProperties.filter(p => p.type === "variableBinding").forEach(p => {
                    let value;
                    const variable = window.apm.getVariable(p.variable.split(".")[1]);
                    switch (variable.props.config.type) {
                        case "BOOLEAN":
                            value = Utility.parseBool(p.default);
                            break;
                        case "NUMBER":
                            value = Number(p.default);
                            break;
                        default:
                            value = p.default ? p.default : null
                    }
                    testProperties[p.config] = value;
                });
                const startRequest = {
                    "type": "testStartSession",
                    "sessionType": this.state.sessionType,
                    "sessionId": this.state.currentSessionId,
                    "testProperties": testProperties,
                    "parameters": {
                        "ani": this.state.ani,
                        "dnis": this.state.dnis,
                        "application": `${this.props.serialized.client}_${this.props.serialized.application}_${this.props.serialized.version}`,
                        "source": this.state.source
                    },
                    "mockGraphQLSchemaNames": mockSchemaIds.map(id => {
                        return this.props.serialized.graphQLSchemas.find(schema => schema.id === id).name;
                    })
                };
                this.setState({replayQueue: []});
                this.appRun(startRequest);
            }
        });
    }

    handleEndSession(){
        const endRequest = {
            "type": "endSession",
            "sessionId": this.state.currentSessionId,
            "endType": "USER_HANGUP"
        };
        clearInterval(this.timer);
        this.appRun(endRequest, () => {
            this.setState({sessionEnded: true})
        });
    }

    handleStartOver(){
        this.setState({
            sessionResults: [],
            replayQueue: [],
            unitTestResults: [],
            sendingRequest: false,
            sessionStarted: false,
            sessionEnded: false
        });
    }

    handleSessionParams(event){
        const name = event.target.name;
        const value = event.target.value;
        
        this.setState({[name]: value})
    }

    handleImport(fileContent) {
        const parsed = JSON.parse(fileContent).filter(test => test.request.type !== "resume");
        this.setState({replayQueue: parsed});
    }

    handleDownload(type){
        const filename = `${this.props.serialized.client}_${this.props.serialized.application}_${this.props.serialized.version}`;
        switch (type) {
            case "unitTests":
                FileSaver.saveAs(new Blob([JSON.stringify(this.state.sessionResults, null, 4)], { type: "text/plain;charset=utf-8" }), `${filename}.json`);
                break;
            case "inputs":
                const lines = [];
                this.state.sessionResults.forEach(result => {
                    const response = result.response;
                    if (response.input) {
                        lines.push(["Input Report", response.input.id]);
                    }
                    if (response.prompts) {
                        response.prompts.forEach(prompt => {
                            lines.push([prompt.name, `"${prompt.transcript}"`]);
                        });
                    }
                });
                FileSaver.saveAs(new Blob([Exporter.toCsv(lines)], { type: "data:text/csv;charset=utf-8" }), `${filename}.csv`);
                break;
        }
    }

    handleFilter(event) {
        const filterState = this.state.filterTypes;
        filterState[event.target.name] = event.target.checked;
        let preset = null;
        for (let p in filterPresets) {
            if (filterPresets.hasOwnProperty(p) && JSON.stringify(filterPresets[p]) === JSON.stringify(filterState)) {
                preset = p;
            }
        }
        this.setState({
            filterPreset: preset,
            filterTypes: filterState
        });
    }

    handleFilterPreset(preset) {
        preset = filterPresets[preset] ? preset : "designer";
        this.setState({
            filterPreset: preset,
            filterTypes: {...filterPresets[preset]}
        });
    }

    handleRunTests(tests) {
        this.setState({
            autoResponse: false,
            unitTestResults: tests.map(t => {
                const test = this.state.unitTests.find(ut => ut.id === t);
                const currentSessionId = this.state.currentSessionId || this.generateSessionID();
                return {
                    id: test.id,
                    sessionId: currentSessionId,
                    name: test.name,
                    queue: JSON.parse(JSON.stringify(test.tests)),
                    results: {
                        currentSessionId: null,
                        sessionType: null,
                        ani: null,
                        dnis: null,
                        source: null,
                        sessionResults: []
                    },
                    total: test.tests.length,
                    passed: 0,
                    failed: 0
                }
            })
        }, () => {
            this.test();
        });
    }

    handleShowTest(test) {
        if (test.results) {
            this.setState(test.results);
        }
    }

    render() {
        
        const graphQLSchemas = this.state.graphQLSchemas;

        return (
            <div style={{margin: '2em'}}>
                <div className="test-tool-container">
                    {/* TODO: */}
                    {/* both start session and run session panels */}
                    {/* props: session start data, session run data (intents options, selected intent, itent onchange function, 
                        intent value, star session, submit intent and end session handlers, download tests and input/prompts handlers) */}
                    <SessionPanel
                        {...this.state}
                        useMockDataToggle={this.useMockDataToggle}
                        channelOptions={channelOptions}
                        errorOptions={errorOptions}
                        graphQLSchemas={graphQLSchemas}
                        handleEndpoint={this.handleEndpoint}
                        handleChannelSelect={(newSessionType) => {
                            this.setState({ sessionType: newSessionType });
                        }}
                        handleStartSession={this.handleStartSession}
                        handleEndSession={this.handleEndSession}
                        handleStartOver={this.handleStartOver}
                        handleSessionParams={this.handleSessionParams}
                        handleIntentName={this.handleIntentName}
                        handleIntentValue={this.handleIntentValue}
                        handleRemoveIntent={this.handleRemoveIntent}
                        handleAddIntent={this.handleAddIntent}
                        handleRunIntent={this.handleRunIntent}
                        handleErrorSelect={(newSelectedError) => {
                            this.setState({selectedError: newSelectedError});
                        }}
                        handleSubmitError={this.handleSubmitError}
                        handleImport={this.handleImport}
                        handleDownload={this.handleDownload}
                        handleRunTests={this.handleRunTests}
                        handleShowTest={this.handleShowTest}
                    />

                    {/* session results */}
                    {/* props: session-start data, scroll data  */}
                    <div className="results-container">
                        <div className="session-header">
                            <h3>IVA Results</h3>
                            <div className="session-metadata">
                                <div className="item">
                                    <span className="label">Session ID</span>
                                    <span className="value" style={{userSelect: 'all'}}>{this.state.currentSessionId && this.state.currentSessionId || '---'}</span>
                                </div>
                                <div className="item">
                                    <span className="label">Channel</span>
                                    <span className="value">{this.state.sessionStarted && this.state.channel || '---'}</span>
                                </div>
                                <div className="item">
                                    <span className="label">Source</span>
                                    <span className="value">{this.state.sessionStarted && this.state.source || '---'}</span>
                                </div>
                                <div className="item">
                                    <span className="label">ANI</span>
                                    <span className="value">{this.state.sessionStarted && this.state.ani || '---'}</span>
                                </div>
                                <div className="item">
                                    <span className="label">DNIS</span>
                                    <span className="value">{this.state.sessionStarted && this.state.dnis || '---'}</span>
                                </div>
                            </div>        
                        </div>
                        <div className="session-results">
                            <div className="results-scroller">
                                {this.state.sessionResults.length > 0 &&
                                    this.state.sessionResults.map((result, idx) => {
                                        return <ResponseListGroup keyIdx={idx+1} listItem={result} key={idx} filter={this.state.filterTypes} filterInfo={filterInfo}/>
                                    })
                                }
                                <div style={{ float:"left", clear: "both" }}
                                     ref={(el) => { this.messagesEnd = el; }}>
                                </div>
                            </div>
                            <div className="results-filter">
                                <div className="item">
                                    <label>Prompt TTS Playback</label>
                                    <VoiceDropdown
                                        audioType={AudioType.tts}
                                        languageCode={defaultLanguage}
                                        onChange={async (newVoice) => this.setState({ voice: newVoice })}
                                        value={this.state.voice}
                                    />
                                </div>
                                <div className="item">
                                    <label>Filters</label>
                                </div>
                                {
                                    Object.keys(this.state.filterTypes).map(type => {
                                        return <div className="item" key={type}>
                                            <span><input type="checkbox" name={type} checked={this.state.filterTypes[type]} onChange={this.handleFilter}/></span>
                                            <span className="checkbox-label"><i className={"fe " + filterInfo[type].iconClass}/> {filterInfo[type].name}</span>
                                        </div>
                                    })
                                }
                                <div className="linkContainer">
                                    <br/>
                                    <a href='#' onClick={event => {
                                        event.preventDefault();
                                        this.handleFilterPreset("clear");
                                    }}>Clear</a>
                                    <br/><br/>
                                    <a className={this.state.filterPreset === "client" ? "selected" : ""} href='#' onClick={event => {
                                        event.preventDefault();
                                        this.handleFilterPreset("client");
                                    }}>Client View</a>
                                    <br/><br/>
                                    <a className={this.state.filterPreset === "designer" ? "selected" : ""} href='#' onClick={event => {
                                        event.preventDefault();
                                        this.handleFilterPreset("designer")
                                    }}>Designer View</a>
                                    <br/><br/>
                                    <a className={this.state.filterPreset === "developer" ? "selected" : ""} href='#' onClick={event => {
                                        event.preventDefault();
                                        this.handleFilterPreset("developer");
                                    }}>Developer View</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
        
    }
}
 
